import type { ComponentProps, ComponentType, SVGProps } from "react";
import type { RequestListItemStatusEnumKey } from "@synthesis/shared-services/external/application/types";
import type { InternalRequestDetailResponseStatusEnumKey } from "@synthesis/shared-services/internal/application/types";
import {
  IconApplicationStatusApprovalDocument,
  IconApplicationStatusCommission,
  IconApplicationStatusDecisionForming,
  IconApplicationStatusDraft,
  IconApplicationStatusExport,
  IconApplicationStatusImport,
  IconApplicationStatusIntakeCheck,
  IconApplicationStatusNgOffice,
  IconApplicationStatusNtpLocation,
  IconApplicationStatusRejectedCircle,
  IconApplicationStatusRevision,
  IconApplicationStatusSentToRzd,
  IconApplicationStatusSuccessCircle,
  IconApplicationStatusUserAssigned,
  IconApplicationStatusWarningTriangle,
  IconPayment,
  IconCheckmark,
  IconCornerUpLeftLine,
} from "@synthesis/shared-icons";
import { Tag } from "@synthesis/shared-ui";

import {
  APPLICATION_STATUS_FALLBACK_LABEL,
  EXTERNAL_APPLICATION_STATUS_FALLBACK_LABEL,
  INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL,
  type ApplicationStatus,
} from "../constants";

type UiStatus = NonNullable<ComponentProps<typeof Tag>["status"]>;

export { APPLICATION_STATUS_FALLBACK_LABEL, EXTERNAL_APPLICATION_STATUS_FALLBACK_LABEL, INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL };
export type { ApplicationStatus };

export const EXTERNAL_APPLICATION_STATUS_TO_UI_STATUS: Record<RequestListItemStatusEnumKey, UiStatus> = {
  DRAFT: "NEUTRAL",
  IN_REVIEW: "IN_QUEUE",
  REVISION_REQUIRED: "ERROR",
  WAITING_PAYMENT: "TO_REUPLOAD",
  RESOLVED_APPROVED: "SUCCESS",
  RESOLVED_REJECTED: "ERROR",
};

export const INTERNAL_APPLICATION_STATUS_TO_UI_STATUS: Record<InternalRequestDetailResponseStatusEnumKey, UiStatus> = {
  INTAKE_CHECK: "IN_QUEUE",
  REVISION_REQUIRED: "TO_REUPLOAD",
  SENT_TO_NG: "IN_QUEUE",
  IN_NG: "IN_QUEUE",
  SENT_TO_NTP: "IN_QUEUE",
  IN_NTP: "IN_QUEUE",
  NTP_RESP_ASSIGNED: "IN_QUEUE",
  COMMISSION_PREP: "IN_QUEUE",
  DECISION_FORMING: "IN_QUEUE",
  NTP_APPROVAL: "IN_QUEUE",
  NG_APPROVAL: "IN_QUEUE",
  DECISION_REWORK: "TO_REUPLOAD",
  TU_ISSUED: "SUCCESS",
  REJECTED: "ERROR",
};

export const APPLICATION_STATUS_TO_UI_STATUS: Record<ApplicationStatus, UiStatus> = {
  ...EXTERNAL_APPLICATION_STATUS_TO_UI_STATUS,
  ...INTERNAL_APPLICATION_STATUS_TO_UI_STATUS,
};

type StatusIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export type ApplicationStatusAppearance = {
  className: string;
  icon: StatusIconComponent;
};

export type ApplicationStatusContour = "internal" | "external";

export type ApplicationProcessingState = "REQUIRES_EXPORT" | "EXPORTED" | "IMPORTED";

const INTERNAL_APPLICATION_STATUS_APPEARANCE: Record<InternalRequestDetailResponseStatusEnumKey, ApplicationStatusAppearance> = {
  INTAKE_CHECK: {
    className: "bg-additional-blue-90 text-additional-blue-50",
    icon: IconApplicationStatusIntakeCheck,
  },
  REVISION_REQUIRED: {
    className: "bg-system-attention-90 text-system-attention-50",
    icon: IconApplicationStatusRevision,
  },
  SENT_TO_NG: {
    className: "bg-system-info-90 text-system-info-50",
    icon: IconApplicationStatusNgOffice,
  },
  IN_NG: {
    className: "bg-additional-yellow-90 text-additional-yellow-50",
    icon: IconApplicationStatusNgOffice,
  },
  SENT_TO_NTP: {
    className: "bg-additional-violet-90 text-additional-violet-50",
    icon: IconApplicationStatusNtpLocation,
  },
  IN_NTP: {
    className: "bg-additional-orange-90 text-additional-orange-50",
    icon: IconApplicationStatusNtpLocation,
  },
  NTP_RESP_ASSIGNED: {
    className: "bg-additional-olive-90 text-additional-olive-50",
    icon: IconApplicationStatusUserAssigned,
  },
  COMMISSION_PREP: {
    className: "bg-additional-turquoise-90 text-additional-turquoise-50",
    icon: IconApplicationStatusCommission,
  },
  DECISION_FORMING: {
    className: "bg-additional-turquoise-90 text-additional-turquoise-50",
    icon: IconApplicationStatusDecisionForming,
  },
  NTP_APPROVAL: {
    className: "bg-tertiary-95 text-tertiary-60",
    icon: IconApplicationStatusApprovalDocument,
  },
  NG_APPROVAL: {
    className: "bg-additional-blue-90 text-additional-blue-50",
    icon: IconApplicationStatusApprovalDocument,
  },
  DECISION_REWORK: {
    className: "bg-system-attention-90 text-system-attention-50",
    icon: IconApplicationStatusRevision,
  },
  TU_ISSUED: {
    className: "bg-system-success-90 text-system-success-50",
    icon: IconApplicationStatusSuccessCircle,
  },
  REJECTED: {
    className: "bg-system-error-90 text-system-error-50",
    icon: IconApplicationStatusRejectedCircle,
  },
};

const EXTERNAL_APPLICATION_STATUS_APPEARANCE: Record<RequestListItemStatusEnumKey, ApplicationStatusAppearance> = {
  DRAFT: {
    className: "bg-neutrals-90 text-neutrals-40",
    icon: IconApplicationStatusDraft,
  },
  IN_REVIEW: {
    className: "bg-additional-blue-90 text-additional-blue-50",
    icon: IconApplicationStatusSentToRzd,
  },
  REVISION_REQUIRED: {
    className: "bg-system-attention-90 text-system-attention-50",
    icon: IconApplicationStatusWarningTriangle,
  },
  WAITING_PAYMENT: {
    className: "bg-additional-olive-90 text-additional-olive-50",
    icon: IconPayment,
  },
  RESOLVED_APPROVED: {
    className: "bg-system-success-90 text-system-success-50",
    icon: IconApplicationStatusSuccessCircle,
  },
  RESOLVED_REJECTED: {
    className: "bg-system-error-90 text-system-error-50",
    icon: IconApplicationStatusRejectedCircle,
  },
};

export const APPLICATION_PROCESSING_STATE_APPEARANCE: Record<ApplicationProcessingState, ApplicationStatusAppearance> = {
  REQUIRES_EXPORT: {
    className: "bg-system-attention-90 text-system-attention-50",
    icon: IconApplicationStatusWarningTriangle,
  },
  EXPORTED: {
    className: "bg-additional-blue-90 text-additional-blue-50",
    icon: IconApplicationStatusExport,
  },
  IMPORTED: {
    className: "bg-system-success-90 text-system-success-50",
    icon: IconApplicationStatusImport,
  },
};

export const getApplicationStatusAppearance = (
  status: ApplicationStatus,
  contour: ApplicationStatusContour = "internal",
): ApplicationStatusAppearance => {
  if (contour === "external" && status in EXTERNAL_APPLICATION_STATUS_APPEARANCE) {
    return EXTERNAL_APPLICATION_STATUS_APPEARANCE[status as RequestListItemStatusEnumKey];
  }

  return INTERNAL_APPLICATION_STATUS_APPEARANCE[status as InternalRequestDetailResponseStatusEnumKey];
};

export const RESOLUTION_BADGE_BG_CLASS = {
  APPROVED: "bg-system-success-90",
  REJECTED: "bg-system-error-90",
  PENDING: "bg-neutrals-95",
} as const;

export const RESOLUTION_BADGE_TEXT_CLASS = {
  APPROVED: "text-system-success-50",
  REJECTED: "text-system-error-50",
  PENDING: "text-type-minor",
} as const;

export const RESOLUTION_BADGE_TEXT = {
  APPROVED: "Согласовано",
  REJECTED: "Отклонено",
  PENDING: "Ожидает решения",
} as const;

/** Исход согласования этапа итогового решения (НТП / Аппарат НГ). */
export type FinalDecisionApproverOutcome = "APPROVED" | "RETURNED";

export const FINAL_DECISION_APPROVER_OUTCOME_LABEL: Record<FinalDecisionApproverOutcome, string> = {
  APPROVED: "Согласовано",
  RETURNED: "Возвращено",
} as const;

export const FINAL_DECISION_APPROVER_OUTCOME_TO_UI_STATUS: Record<FinalDecisionApproverOutcome, UiStatus> = {
  APPROVED: "SUCCESS",
  RETURNED: "TO_REUPLOAD",
} as const;

export const FINAL_DECISION_APPROVER_OUTCOME_ICON: Record<FinalDecisionApproverOutcome, StatusIconComponent> = {
  APPROVED: IconCheckmark,
  RETURNED: IconCornerUpLeftLine,
} as const;
